<template>
  <div class="rangeslider-layout">
    <span class="min-label">{{ computedMinLabel }}</span>
    <div class="rangeslider" ref="slider">
      <input
        type="range"
        :min="min"
        :max="max"
        v-model="displayedValue"
        @input="updateValue"
        ref="rangeInput"
        class="rangeslider__input"
      />
      <div class="rangeslider__track" :style="{ width: getThumbPosition() }"></div>
      <div ref="handle" class="rangeslider__handle" :style="{ left: getThumbPosition() }">
        {{ displayedValue }}
      </div>
    </div>
    <span class="max-label">{{ computedMaxLabel }}</span>
  </div>
</template>

<script>
import { state } from '@/lib/state'
export default {
  props: {
    min: {
      type: Number,
      required: true,
    },
    max: {
      type: Number,
      required: true,
    },
    minLabel: {
      type: String,
      default: '',
    },
    maxLabel: {
      type: String,
      default: '',
    },
    stateKey: {
      type: String,
      required: true,
    },
  },
  computed: {
    computedMinLabel() {
      return this.minLabel || this.min
    },
    computedMaxLabel() {
      return this.maxLabel || this.max
    },
    displayedValue() {
      const list = state.lists[state.listKeys[this.stateKey]]
      return list?.find((item) => item.id === state.answers[this.stateKey])?.text || this.min
    },
  },
  mounted() {
    this.updateHandle() // Initialize the handle with the starting value
    window.addEventListener('resize', this.updateHandle) // Update handle position on window resize
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateHandle) // Clean up event listener
  },
  methods: {
    updateValue(event) {
      const value = event.target.value

      const list = state.lists[state.listKeys[this.stateKey]]
      // Compare as numbers to avoid type issues
      const selectedOption = list?.find((item) => parseFloat(item.text) === parseFloat(value))

      if (!selectedOption?.id) {
        console.error('Could not find id for value', value)
        return
      }

      // Store the selected value's id and text
      state.answers[this.stateKey] = selectedOption.id
      state.displayAnswers[this.stateKey] = selectedOption.text || value
      this.$emit('input', value)
      this.updateHandle()
    },
    getThumbPosition() {
      if (!this.$refs.slider) return '0%'

      const rangeInput = this.$refs.rangeInput
      const value = (rangeInput.value - rangeInput.min) / (rangeInput.max - rangeInput.min)
      const thumbWidth = this.$refs.handle.offsetWidth
      const sliderWidth = this.$refs.slider.offsetWidth
      return `${value * (sliderWidth - thumbWidth)}px`
    },
    updateHandle() {
      const handle = this.$refs.handle
      if (handle) {
        handle.style.left = this.getThumbPosition()
      }
    },
  },
}
</script>

<style scoped>
.rangeslider-layout {
  display: flex;
  align-items: center;
  padding-top: 18px;
  --track-height: 2px;
}
.rangeslider {
  position: relative;
  width: 100%;
}

.rangeslider__track {
  width: 50%;
  height: var(--track-height);

  border-radius: 3px;
  position: absolute;
  bottom: -8px;
  left: 0;
}

input[type='range']:active::-webkit-slider-thumb {
  cursor: grabbing;
}

input[type='range']::-webkit-slider-runnable-track {
  background: linear-gradient(var(--color-darkblue) 0 0) scroll no-repeat center / 100%
    calc(var(--track-height) + 1px);
  border: 8px solid transparent;
}

input[type='range']::-moz-range-progress {
  background-color: var(--color-white);
  height: var(--track-height);
}

input[type='range']::-webkit-progress-value {
  background-color: var(--color-darkblue);
}

.rangeslider__input {
  width: 100%;
  outline: none;
  -webkit-appearance: none; /* Hide the default browser handle */
  appearance: none; /* Hide the default browser handle */
  background: transparent; /* Make the background transparent */
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  cursor: pointer;
  margin: 0;
}

.rangeslider__handle {
  position: absolute;
  top: -42px;
  border-radius: 50%;
  font-size: 16;
  text-align: center;
  width: 32px;
  height: 32px;
  line-height: 32px;
  pointer-events: none;
}

.labels {
  display: flex;
  justify-content: space-between;
  font-size: var(--font-size-body);
  color: var(--color-white);
}

.min-label,
.max-label {
  font-size: var(--font-size-small);
  line-height: 32px;
  font-size: var(--font-size-body);
}
</style>
